// build-marker: 2026-08-30T15-05-seal-owned-quantity-cadence
(function attachBrasaPurchaseFlow(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.BrasaPurchaseFlow = api;

  const start = () => api.mount(document, root);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})(typeof window !== 'undefined' ? window : globalThis, function createBrasaPurchaseFlow() {
  'use strict';

  // 2026-08-30 (session 10): quantity maps deterministically to cadence, and
  // every plan below is SEAL-OWNED. Seal Subscriptions owns selling plan group
  // 2093088935 ("Monthly refill · save 25%") and is the only installed app that
  // creates recurring billing attempts, so a plan Seal does not own bills once
  // and never renews. Group 2093088935 now carries four Seal plans, all 25%
  // off, created inside the Seal app (Subscription rules -> BRASA monthly
  // refill -> four selling plans):
  //   3891822759  DAY/30   1 tin
  //   4181033127  DAY/60   2 tins
  //   4181065895  DAY/90   3 tins
  //   4181098663  DAY/120  4 tins
  // One tin is ~30 days of daily strips, so shipping N tins every N*30 days
  // matches supply to consumption instead of overstocking the customer.
  // The four merchant-created plans in group 2279702695 (4178575527 /
  // 4178608295 / 4178641063 / 4178673831) have appId null - no app drives
  // them - and that group is now detached from every BRASA product. Never
  // reintroduce a selling plan that Seal does not own.
  const SUBSCRIPTION_STAGING_VARIANT_ID = '50936727175335';
  const SUBSCRIPTION_PLAN_IDS_BY_QTY = {
    1: '3891822759',
    2: '4181033127',
    3: '4181065895',
    4: '4181098663',
  };
  // Must stay in lockstep with the billing/delivery interval of the plan on the
  // same line above. Never quote a cadence Seal is not actually billing.
  const SUBSCRIPTION_CADENCE_DAYS_BY_QTY = { 1: 30, 2: 60, 3: 90, 4: 120 };
  // One-time ladder ($39.99 / $72.00 / $99.00 / $129.00). 1 tin adds the single
  // staging product; 2/3/4 tins add the matching component-tracked bundle
  // product (always quantity 1). The bundle price is a direct product field,
  // so PDP, cart and checkout cannot disagree and no automatic discount is
  // involved. Inventory is drawn from the single product's stock through the
  // bundle components.
  const ONE_TIME_VARIANT_IDS_BY_QTY = Object.freeze({
    1: '50936727175335',
    2: '51277320093863',
    3: '51277320126631',
    4: '51277320159399',
  });

  // The single source of truth for what the cart form submits.
  function resolveCartContract({ mode, quantity }) {
    const qty = clampSubscribableQuantity(quantity);
    if (mode === 'subscription') {
      return {
        variantId: SUBSCRIPTION_STAGING_VARIANT_ID,
        quantity: qty,
        sellingPlanId: sellingPlanIdForQuantity(qty),
      };
    }
    return { variantId: ONE_TIME_VARIANT_IDS_BY_QTY[qty], quantity: 1, sellingPlanId: '' };
  }
  const SUBSCRIPTION_MAX_QUANTITY = 4;
  // Mirrors the Seal plans (25% off on every cadence). Price is derived from
  // the subscribable tier, not hard-coded.
  const SUBSCRIPTION_MULTIPLIER = 0.75;
  const SUBSCRIPTION_SAVE_LABEL = 'Save $10.00';

  function clampSubscribableQuantity(quantity) {
    return Math.max(1, Math.min(SUBSCRIPTION_MAX_QUANTITY, Number.parseInt(quantity, 10) || 1));
  }

  function sellingPlanIdForQuantity(quantity) {
    const qty = clampSubscribableQuantity(quantity);
    return SUBSCRIPTION_PLAN_IDS_BY_QTY[qty] || SUBSCRIPTION_PLAN_IDS_BY_QTY[1];
  }

  function cadenceDaysForQuantity(quantity) {
    const qty = clampSubscribableQuantity(quantity);
    return SUBSCRIPTION_CADENCE_DAYS_BY_QTY[qty] || 30;
  }

  const COPY = Object.freeze({
    purchaseOptions: 'Choose how to order',
    oneTime: 'Buy once',
    subscription: 'Refill subscription · save 25%',
    description: 'One tin per 30 days of use. Free shipping. Skip or cancel anytime.',
    subscriptionCta: 'Add subscription to cart',
    subscriptionDetails: 'How refills work',
    oneTimeSubtitle: 'One time. Pay once.',
    subscriptionSubtitle: 'Ships one tin per 30 days of use.',
    ladderNote: 'Refills ship one tin per 30 days of use: 1 tin every 30 days, 2 every 60, 3 every 90, 4 every 120. Manage or cancel anytime.',
    toggleTitle: 'Subscribe & save',
    toggleSub: '1 tin · $29.99 every 30 days.\nSkip or cancel anytime.',
    togglePer: '/30d',
  });

  function parseMoney(text) {
    const n = Number.parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  function formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  function getSelectedTierPrice(doc) {
    const priceText = doc.querySelector('.quantity-break-option.selected .quantity-break-price')?.textContent
      || doc.querySelector('.shop-product-price-block')?.textContent;
    return parseMoney(priceText);
  }

  // List price of a single tin (the 1-tin tier). When the ladder is showing
  // subscription prices, read the stashed one-time base so the top price is
  // never computed off an already-discounted number (no double discount).
  function getListPerTin(doc) {
    const oneTin = [...doc.querySelectorAll('.quantity-break-input')]
      .find((input) => Number.parseInt(input.value, 10) === 1);
    const priceEl = oneTin?.closest('.quantity-break-option')?.querySelector('.quantity-break-price');
    return parseMoney(priceEl?.dataset.brasaListPrice || priceEl?.textContent);
  }

  // Seal discounts 25% off EACH tin (the list per-tin price), rounded per unit,
  // and does NOT stack with the one-time bundle discounts. Round the unit first
  // so the displayed math matches what Seal actually charges to the cent
  // (e.g. $39.99 -> $29.99/tin -> 2:$59.98  3:$89.97  4:$119.96).
  // Pure + testable: the per-tin subscription price Seal charges (25% off list,
  // rounded per unit). Exported so the exact cart numbers are regression-tested.
  function subscriptionUnitPrice(listPerTin) {
    if (listPerTin == null || !Number.isFinite(listPerTin)) return null;
    return Math.round(listPerTin * SUBSCRIPTION_MULTIPLIER * 100) / 100;
  }

  function subscriptionUnit(doc) {
    return subscriptionUnitPrice(getListPerTin(doc));
  }

  /* Sticky CTA carries the live tier price, the way the quiz CTA already does
     ("Shop my match - $99"). A price-less sticky button makes the buyer click to
     find out what it costs. Ritual puts the price in the button; so do we now.
     Nick 2026-08-27. */
  function selectedTierPriceText(doc) {
    const opt = getSelectedTierInput(doc)?.closest('.quantity-break-option');
    const t = opt?.querySelector('.quantity-break-price')?.textContent?.trim();
    return t && /\d/.test(t) ? t.replace(/\.00$/, '') : null;
  }

  function syncStickyCta(doc) {
    const price = selectedTierPriceText(doc);
    doc.querySelectorAll('.sticky-add-to-cart__button').forEach((btn) => {
      const base = btn.getAttribute('data-brasa-base') || btn.textContent.trim().split(/\s+[-\u2014]\s+/)[0];
      if (!btn.getAttribute('data-brasa-base')) btn.setAttribute('data-brasa-base', base);
      const next = price ? base + ' \u2014 ' + price : base;
      if (btn.textContent.trim() !== next) btn.textContent = next;
    });
  }

  function subscriptionTotal(doc) {
    // Every tier bills on the same Seal 30-day cadence, so the toggle quotes
    // the exact charge for the currently selected qty.
    const unit = subscriptionUnit(doc);
    if (unit == null) return null;
    const qty = getSelectedQuantity(doc);
    return Math.round(unit * qty * 100) / 100;
  }

  // Mirror the selected tier card exactly (its already-discounted price + struck
  // original), so the top price can never drift from the ladder or the cart.
  function updateTopPriceForSubscription(doc) {
    const option = getSelectedTierInput(doc)?.closest('.quantity-break-option');
    if (!option) return;
    const price = option.querySelector('.quantity-break-price')?.textContent.trim();
    const original = option.querySelector('.quantity-break-original-price')?.textContent.trim();
    const badge = option.querySelector('.quantity-break-save-badge')?.textContent.replace(/\s+/g, ' ').trim();
    if (price) updateTopPrice(doc, price, original || '', badge || SUBSCRIPTION_SAVE_LABEL);
  }

  /*
    Reflect the subscription discount on EVERY tier card (not just the top
    price) so a shopper sees the per-package subscription price when the box is
    ticked — the pattern the competitor PDPs use. Display-only: the real charge
    comes from Seal's selling plan server-side. The struck price is the regular
    Shopify price, not MSRP, so the displayed comparison is exactly 25%.
    Idempotent + reversible, and
    self-healing across the quantity-break app's re-renders:
      - subscription on : capture the one-time base once, then show base * 0.75
      - subscription off: restore the stored one-time base
    The brasaSubApplied flag prevents compounding the discount on observer ticks;
    a fresh (re-rendered) node has no flag, so its one-time text is recaptured.
  */
  // The tier gift bars ("+ Free · The Scalp Ritual Comb") sit next to the
  // option label, not inside it. Gifts are one-time only — the submit handler
  // clears form.dataset.giftVariants on subscription — so leaving the bars
  // visible advertises a gift the cart will not add. Hide them while
  // subscription is active and restore them on the way back.
  function setGiftBarsForMode(opt, subscription) {
    const bars = opt.parentElement?.querySelector(':scope > .quantity-break-gift-bars');
    if (!bars) return;
    // The snippet renders these with an inline `display:flex`, which beats the
    // [hidden] UA rule — so toggle the inline display, not the attribute.
    if (subscription) {
      if (bars.dataset.brasaGiftsHiddenForSub !== 'true') {
        bars.dataset.brasaOneTimeGiftsDisplay = bars.style.display || '';
        bars.style.display = 'none';
        bars.dataset.brasaGiftsHiddenForSub = 'true';
      }
    } else if (bars.dataset.brasaGiftsHiddenForSub === 'true') {
      bars.style.display = bars.dataset.brasaOneTimeGiftsDisplay || '';
      bars.dataset.brasaGiftsHiddenForSub = 'false';
    }
  }

  function setLadderPricesForMode(doc, subscription) {
    const ladder = doc.querySelector('[id$="__shop_product_details_JbqzwH"] .quantity-break-form');
    if (ladder) {
      let comparison = ladder.parentElement.querySelector('.brasa-savings-comparison');
      if (!comparison) {
        comparison = doc.createElement('p');
        comparison.className = 'brasa-savings-comparison';
        comparison.textContent = 'Savings compared with one-time purchase.';
        ladder.insertAdjacentElement('afterend', comparison);
      }
      comparison.hidden = !subscription;
    }
    const regularUnit = getListPerTin(doc);
    const unit = subscriptionUnitPrice(regularUnit);
    const options = doc.querySelectorAll('.quantity-break-option');
    options.forEach((opt) => {
      setGiftBarsForMode(opt, subscription);
      const el = opt.querySelector('.quantity-break-price');
      const originalEl = opt.querySelector('.quantity-break-original-price');
      let saveBadge = opt.querySelector('.quantity-break-save-badge');
      const savingsEl = opt.querySelector('.quantity-break-savings');
      if (!el) return;
      if (subscription) {
        // Stash every one-time-only label once so unticking is lossless.
        if (el.dataset.brasaSubApplied !== 'true') {
          el.dataset.brasaListPrice = el.textContent.trim();
          if (originalEl) {
            originalEl.dataset.brasaOneTimeComparePrice = originalEl.textContent.trim();
            originalEl.dataset.brasaOneTimeCompareHidden = originalEl.hidden ? 'true' : 'false';
          }
          if (saveBadge) {
            saveBadge.dataset.brasaOneTimeText = saveBadge.textContent.trim();
            saveBadge.dataset.brasaOneTimeHidden = saveBadge.hidden ? 'true' : 'false';
          }
          if (savingsEl) {
            savingsEl.dataset.brasaOneTimeHidden = savingsEl.hidden ? 'true' : 'false';
          }
        }
        if (unit == null || regularUnit == null) return;
        const qty = Math.max(1, Number.parseInt(opt.querySelector('.quantity-break-input')?.value, 10) || 1);
        // 25% off list per tin × this tier's qty — NOT the bundle price × 0.75.
        const next = formatMoney(unit * qty);
        if (el.textContent.trim() !== next) el.textContent = next;
        // Compare against THIS tier's one-time price (the offer the customer was
        // looking at), never $39.99 x qty -- a 3-tin buyer would not have paid $119.97.
        const oneTimeTier = parseMoney(el.dataset.brasaListPrice);
        const oneTimeTotal = oneTimeTier != null ? oneTimeTier : regularUnit * qty;
        if (originalEl) {
          const oneTimeText = formatMoney(oneTimeTotal);
          if (originalEl.textContent.trim() !== oneTimeText) originalEl.textContent = oneTimeText;
          originalEl.hidden = false;
        }
        if (!saveBadge) {
          const titleContainer = opt.querySelector('.quantity-break-title-container');
          if (titleContainer) {
            saveBadge = doc.createElement('div');
            saveBadge.className = 'quantity-break-save-badge';
            saveBadge.dataset.brasaSubCreated = 'true';
            titleContainer.append(saveBadge);
          }
        }
        if (saveBadge) {
          const savings = Math.round((oneTimeTotal - unit * qty) * 100) / 100;
          saveBadge.textContent = qty === 1
            ? SUBSCRIPTION_SAVE_LABEL
            : `Save ${formatMoney(Math.max(0, savings))}`;
          saveBadge.hidden = false;
        }
        // These subtitles describe the one-time bundle's per-strip math. Hide
        // them in subscription mode instead of presenting a conflicting number.
        if (savingsEl) savingsEl.hidden = !/^\s*(?:\d+ tins?\s*·\s*)?\d+[- ]day supply\s*$/i.test(savingsEl.textContent);
        el.dataset.brasaSubApplied = 'true';
      } else if (el.dataset.brasaSubApplied === 'true') {
        const base = el.dataset.brasaListPrice;
        if (base && el.textContent.trim() !== base) el.textContent = base;
        if (originalEl) {
          const compare = originalEl.dataset.brasaOneTimeComparePrice;
          if (compare && originalEl.textContent.trim() !== compare) originalEl.textContent = compare;
          originalEl.hidden = originalEl.dataset.brasaOneTimeCompareHidden === 'true';
        }
        if (saveBadge?.dataset.brasaSubCreated === 'true') {
          saveBadge.remove();
        } else if (saveBadge) {
          const oneTimeText = saveBadge.dataset.brasaOneTimeText;
          if (oneTimeText) saveBadge.textContent = oneTimeText;
          saveBadge.hidden = saveBadge.dataset.brasaOneTimeHidden === 'true';
        }
        if (savingsEl) {
          savingsEl.hidden = savingsEl.dataset.brasaOneTimeHidden === 'true';
        }
        el.dataset.brasaSubApplied = 'false';
      }
    });
  }

  function resolvePurchaseSelection({
    requestedMode,
    requestedQuantity,
    previousOneTimeQuantity,
    restorePrevious = false,
    sellingPlanId,
  }) {
    const mode = requestedMode === 'subscription' ? 'subscription' : 'one_time';
    const parsedRequestedQuantity = Math.max(1, Number.parseInt(requestedQuantity, 10) || 1);
    const parsedPreviousQuantity = Math.max(1, Number.parseInt(previousOneTimeQuantity, 10) || 1);

    if (mode === 'subscription') {
      // Quantity is never clamped down anymore: the Seal plan discounts any
      // quantity, so the selected tier survives the toggle unchanged. The plan
      // id is always derived internally (never from the caller), so a stale or
      // foreign plan id can never be submitted.
      const quantity = clampSubscribableQuantity(parsedRequestedQuantity);
      return {
        mode,
        quantity,
        sellingPlanId: sellingPlanIdForQuantity(quantity),
      };
    }

    return {
      mode,
      quantity: restorePrevious ? parsedPreviousQuantity : parsedRequestedQuantity,
      sellingPlanId: '',
    };
  }

  function resolveShippingProtectionQuantity({ checked, inCart }) {
    if (checked && !inCart) return 1;
    if (!checked && inCart) return 0;
    return null;
  }

  /*
    Selecting a purchase option re-selects a tier radio, which makes the
    third-party quantity-break app re-render its option list. That detaches the
    focused radio, focus falls back to <body>, and the browser scrolls the
    (now off-screen) body into view — the "viewport jumps to a random section"
    bug. guardScroll pins the scroll position and forces preventScroll on any
    focus/scrollIntoView for the length of the transition, so the toggle never
    moves the page.
  */
  function guardScroll(win, fn) {
    if (!win || typeof win.scrollTo !== 'function') {
      fn();
      return;
    }

    const x = win.scrollX;
    const y = win.scrollY;
    const restore = () => {
      if (win.scrollX !== x || win.scrollY !== y) win.scrollTo(x, y);
    };

    // Re-entrancy guard: never capture an already-patched method as "original".
    if (win.__brasaScrollGuardActive) {
      try {
        fn();
      } finally {
        restore();
        if (typeof win.requestAnimationFrame === 'function') win.requestAnimationFrame(restore);
      }
      return;
    }

    const elementProto = win.Element && win.Element.prototype;
    const htmlProto = win.HTMLElement && win.HTMLElement.prototype;
    const originalFocus = htmlProto && htmlProto.focus;
    const originalScrollIntoView = elementProto && elementProto.scrollIntoView;

    win.__brasaScrollGuardActive = true;
    if (originalFocus) {
      htmlProto.focus = function guardedFocus(options) {
        return originalFocus.call(this, Object.assign({}, options, { preventScroll: true }));
      };
    }
    if (originalScrollIntoView) {
      elementProto.scrollIntoView = function guardedScrollIntoView() {};
    }

    const release = () => {
      restore();
      if (originalFocus) htmlProto.focus = originalFocus;
      if (originalScrollIntoView) elementProto.scrollIntoView = originalScrollIntoView;
      win.__brasaScrollGuardActive = false;
    };

    try {
      fn();
    } finally {
      restore();
      if (typeof win.requestAnimationFrame === 'function') {
        win.requestAnimationFrame(() => {
          restore();
          win.requestAnimationFrame(restore);
        });
      }
      // Keep the guard active long enough to absorb the app's deferred repaint.
      win.setTimeout(release, 400);
    }
  }

  function getSealWidget(doc) {
    return doc.querySelector('.shop-add-to-cart-wrapper .sealsubs-target-element.sealsubs-full')
      || doc.querySelector('.shop-add-to-cart-wrapper .sealsubs-target-element');
  }

  function isPrimarySealWidget(doc, widget) {
    return Boolean(widget && widget === getSealWidget(doc));
  }

  function getSelectedTierInput(doc) {
    return doc.querySelector('.quantity-break-input:checked')
      || doc.querySelector('.quantity-break-option.selected .quantity-break-input');
  }

  function getSelectedQuantity(doc) {
    return Math.max(1, Number.parseInt(getSelectedTierInput(doc)?.value, 10) || 1);
  }

  function getTierInput(doc, quantity) {
    return [...doc.querySelectorAll('.quantity-break-input')]
      .find((input) => Number.parseInt(input.value, 10) === Number.parseInt(quantity, 10));
  }

  function markOneTimeOnlyContent(doc) {
    const quantityHeading = [...doc.querySelectorAll('.shop-product-info [class*="custom-text-block-container-"]')]
      .find((element) => element.textContent.trim().toLowerCase() === 'select quantity');
    quantityHeading?.classList.add('brasa-one-time-only');
  }

  function setLabelText(label, text, preserveSelector) {
    if (!label) return;

    const preserved = preserveSelector ? label.querySelector(preserveSelector) : null;
    label.replaceChildren();
    label.append(docText(label, text));
    if (preserved) {
      label.append(docText(label, ' '));
      label.append(preserved);
    }
  }

  function docText(element, text) {
    return element.ownerDocument.createTextNode(text);
  }

  /*
    Give each option an always-visible subtitle in the same style as the tier
    rows ("$0.83 per strip. A solid start."), so a shopper understands what each
    mode ships before selecting it. Idempotent across Seal repaints.
  */
  function ensureOptionSubtitle(optionContainer, text) {
    if (!optionContainer || !text) return;
    const nameEl = optionContainer.querySelector('.sls-selling-plan-group-name');
    if (!nameEl) return;

    const anchor = nameEl.closest('.sls-selling-plan-group-first-line') || nameEl;
    let subtitle = optionContainer.querySelector('.brasa-option-subtitle');
    if (!subtitle) {
      subtitle = optionContainer.ownerDocument.createElement('span');
      subtitle.className = 'brasa-option-subtitle';
      anchor.insertAdjacentElement('afterend', subtitle);
    }
    if (subtitle.textContent !== text) subtitle.textContent = text;
  }

  /*
    The tier ladder stays visible at all times. Subscription is offered as a
    checkbox directly beneath it (mirrors the Nivara / Bloom pattern), so prices
    never disappear and there is no layout jump. The checkbox drives Seal's
    native (visually hidden) radios; ticking it subscribes the selected quantity
    at 25% off regular price. Idempotent across Seal repaints.
  */
  function ensureSubscribeToggle(doc) {
    let toggle = doc.querySelector('.brasa-subscribe-toggle');
    if (toggle) return toggle;

    const ladder = doc.querySelector('.quantity-break-container');
    const anchor = ladder || getSealWidget(doc);
    if (!anchor || !anchor.parentNode) return null;

    toggle = doc.createElement('label');
    toggle.className = 'brasa-subscribe-toggle';

    const checkbox = doc.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'brasa-subscribe-checkbox';
    checkbox.setAttribute('aria-label', COPY.toggleTitle);

    const copy = doc.createElement('span');
    copy.className = 'brasa-toggle-copy';
    const title = doc.createElement('span');
    title.className = 'brasa-toggle-title';
    title.textContent = COPY.toggleTitle;
    const sub = doc.createElement('span');
    sub.className = 'brasa-toggle-sub';
    sub.textContent = COPY.toggleSub;
    copy.append(title, sub);

    const price = doc.createElement('span');
    price.className = 'brasa-toggle-price';
    const amount = doc.createElement('span');
    amount.className = 'brasa-toggle-amount';
    const per = doc.createElement('span');
    per.className = 'brasa-toggle-per';
    per.textContent = COPY.togglePer;
    price.append(amount, per);

    toggle.append(checkbox, copy, price);
    anchor.parentNode.insertBefore(toggle, anchor.nextSibling);
    return toggle;
  }

  function normalizeSealCopy(widget) {
    if (!widget) return;

    const purchaseLabel = widget.querySelector('.sls-purchase-options-label');
    if (purchaseLabel) purchaseLabel.textContent = COPY.purchaseOptions;

    const oneTimeOption = widget.querySelector('input[data-type="one_time"]')?.closest('.sls-option-container');
    const oneTimeLabel = oneTimeOption?.querySelector('.sls-selling-plan-group-name');
    if (oneTimeLabel) oneTimeLabel.textContent = COPY.oneTime;
    ensureOptionSubtitle(oneTimeOption, COPY.oneTimeSubtitle);

    const subscriptionOption = widget.querySelector('input[data-type="subscription"]')?.closest('.sls-option-container');
    const subscriptionLabel = subscriptionOption?.querySelector('.sls-selling-plan-group-name');
    const savingsBadge = subscriptionLabel?.querySelector('.sls-savings-badge');
    if (savingsBadge) savingsBadge.textContent = SUBSCRIPTION_SAVE_LABEL;
    setLabelText(subscriptionLabel, COPY.subscription, '.sls-savings-badge');
    ensureOptionSubtitle(subscriptionOption, COPY.subscriptionSubtitle);

    const description = subscriptionOption?.querySelector('.sls-selling-plan-description-content');
    if (description) description.textContent = COPY.description;

    const subscriptionPriceBelow = subscriptionOption?.querySelector('.sls-price-below');
    if (subscriptionPriceBelow) subscriptionPriceBelow.textContent = 'per refill';

    const detailsLink = widget.querySelector('.sls-subscription-details-label a');
    if (detailsLink) detailsLink.textContent = COPY.subscriptionDetails;

    widget.dataset.brasaCopyReady = 'true';
  }

  function dispatchSellingPlan(widget, sellingPlanId) {
    if (!widget) return;
    widget.dispatchEvent(new CustomEvent('sealsubs:change_selected_selling_plan_id', {
      detail: { selling_plan_id: String(sellingPlanId || '') },
    }));
  }

  function replaceSaveBadgeText(doc, text) {
    const badge = doc.querySelector('.shop-save-price');
    if (!badge) return;

    const currentLabel = badge.querySelector('.brasa-current-save-text');
    if (currentLabel?.textContent === text) return;

    const preserved = [...badge.children].filter((child) => child.matches('style, svg'));
    badge.replaceChildren(...preserved);
    const label = doc.createElement('span');
    label.className = 'brasa-current-save-text';
    label.textContent = text;
    badge.append(label);
  }

  function updateTopPrice(doc, price, comparePrice, saveText) {
    const priceElement = doc.querySelector('.shop-product-price-block');
    const compareElement = doc.querySelector('.shop-compare-price');

    if (priceElement && price && priceElement.textContent.trim() !== price) {
      priceElement.textContent = price;
    }
    if (compareElement) {
      const nextComparePrice = comparePrice || '';
      if (compareElement.textContent.trim() !== nextComparePrice) {
        compareElement.textContent = nextComparePrice;
      }
      if (compareElement.hidden === Boolean(comparePrice)) {
        compareElement.hidden = !comparePrice;
      }
    }
    if (saveText) replaceSaveBadgeText(doc, saveText);
  }

  function updateTopPriceFromTier(doc) {
    const option = getSelectedTierInput(doc)?.closest('.quantity-break-option');
    if (!option) return;

    const price = option.querySelector('.quantity-break-price')?.textContent.trim();
    const comparePrice = option.querySelector('.quantity-break-original-price')?.textContent.trim();
    const saveText = option.querySelector('.quantity-break-save-badge')?.textContent.replace(/\s+/g, ' ').trim();
    updateTopPrice(doc, price, comparePrice, saveText);
  }

  function updateTopPriceFromSubscription(doc, widget) {
    const subscriptionPrice = widget
      ?.querySelector('.sls-selling-plan-group-price .money')
      ?.textContent.trim();
    const oneTimePrice = widget
      ?.querySelector('.sls-one-time-price .money')
      ?.textContent.trim();
    updateTopPrice(doc, subscriptionPrice, oneTimePrice, SUBSCRIPTION_SAVE_LABEL);
  }

  function setCta(doc, mode) {
    const cta = doc.querySelector('.shop-add-to-cart-button .button-text');
    if (!cta) return;
    const button = cta.closest('.shop-add-to-cart-button');

    if (!cta.dataset.brasaOriginalText) {
      cta.dataset.brasaOriginalText = 'Add to cart';
    }
    const nextText = mode === 'subscription'
      ? COPY.subscriptionCta
      : cta.dataset.brasaOriginalText;
    if (button) {
      const productForm = button.closest('product-form');
      const persistentTextChanged = button.dataset.persistentText !== nextText;
      if (persistentTextChanged) productForm?.buttonTextObserver?.disconnect();
      button.dataset.textOverride = 'true';
      button.dataset.persistentText = nextText;
      if (cta.textContent.trim() !== nextText) cta.textContent = nextText;
      if (persistentTextChanged) productForm?.setupButtonTextObserver?.();
      return;
    }
    if (cta.textContent.trim() !== nextText) cta.textContent = nextText;
  }

  function setSellingPlanFormContract(doc, sellingPlanId) {
    const form = doc.querySelector('.shop-add-to-cart-wrapper form[action*="/cart/add"]');
    if (!form) return;

    const nextPlanId = String(sellingPlanId || '');
    let input = form.querySelector('input[name="selling_plan"]');
    if (nextPlanId && !input) {
      input = doc.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.append(input);
    }
    if (input) {
      input.value = nextPlanId;
      input.disabled = false;
    }
  }

  /*
    Applies the cart contract for (mode, selected tier) to the PDP cart form:
    variant id, cart quantity, selling plan, sticky-bar variant. The
    quantity-break app owns the tier UI only; its items[]/quantity rewrites are
    removed so a tier never becomes N units of the single product. The comb is
    a paid cart add-on, never a baked-in gift, so gift variants are cleared.
  */
  function applyCartContract(doc, mode, selectedQuantity) {
    const form = doc.querySelector('.shop-add-to-cart-wrapper form[action*="/cart/add"]');
    if (!form) return null;
    const contract = resolveCartContract({ mode, quantity: selectedQuantity });

    form.querySelector('.quantity-break-variant-form')?.remove();

    let idInput = form.querySelector('input[name="id"]');
    if (!idInput) {
      idInput = doc.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      form.append(idInput);
    }
    if (idInput.value !== contract.variantId) idInput.value = contract.variantId;
    idInput.disabled = false;

    let quantityInput = form.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      quantityInput = doc.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      form.append(quantityInput);
    }
    const qtyText = String(contract.quantity);
    if (quantityInput.value !== qtyText) quantityInput.value = qtyText;
    form.dataset.quantity = String(selectedQuantity);
    form.dataset.brasaCartVariantId = contract.variantId;
    form.dataset.giftVariants = '';

    setSellingPlanFormContract(doc, contract.sellingPlanId);

    doc.querySelectorAll('#sticky-variant-id').forEach((sticky) => {
      if (sticky.value !== contract.variantId) sticky.value = contract.variantId;
    });
    return contract;
  }

  function setShippingBusy(input, busy) {
    const scope = input.closest('.gb-shipping-protection');
    const label = input.closest('label.switch');
    const spinner = scope?.querySelector('.spin-animation');
    const check = scope?.querySelector('.complete-check');

    input.disabled = busy;
    input.dataset.brasaPending = busy ? 'true' : 'false';
    label?.toggleAttribute('aria-busy', busy);
    if (spinner) spinner.style.display = busy ? 'block' : 'none';
    if (check && busy) check.style.display = 'none';
  }

  function setShippingError(doc, message) {
    const errors = doc.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = message || '';
  }

  function enqueueCartMutation(win, task) {
    if (typeof win.enqueueBrasaCartMutation !== 'function') {
      win.enqueueBrasaCartMutation = (queuedTask) => {
        const previous = win.__brasaCartMutationQueue || Promise.resolve();
        const next = previous.catch(() => undefined).then(queuedTask);
        win.__brasaCartMutationQueue = next.catch(() => undefined);
        return next;
      };
    }
    return win.enqueueBrasaCartMutation(task);
  }

  async function fetchCart(win) {
    const response = await win.fetch(`${win.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart read failed with ${response.status}`);
    return response.json();
  }

  async function refreshCartDrawer(doc, win) {
    const response = await win.fetch(`${win.routes?.cart_url || '/cart'}?section_id=cart-drawer`, {
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`Cart drawer refresh failed with ${response.status}`);

    const html = await response.text();
    const parsed = new win.DOMParser().parseFromString(html, 'text/html');
    const currentDrawer = doc.querySelector('#CartDrawer');
    const nextDrawer = parsed.querySelector('#CartDrawer');
    if (!currentDrawer || !nextDrawer) return;

    if (typeof win.updateCartDrawer === 'function') {
      win.updateCartDrawer(currentDrawer, nextDrawer, true);
    } else {
      currentDrawer.replaceChildren(...nextDrawer.cloneNode(true).childNodes);
    }
  }

  async function updateShippingProtection(input, doc, win) {
    if (input.dataset.brasaPending === 'true') return;

    const desiredChecked = input.checked;
    const form = doc.querySelector('form.shipping-protection-form');
    const variantId = form?.querySelector('input[name="id"]')?.value || win.shippingProductId;
    if (!variantId) {
      input.checked = !desiredChecked;
      setShippingError(doc, 'Shipping protection is unavailable right now.');
      return;
    }

    setShippingBusy(input, true);
    setShippingError(doc, '');

    try {
      await enqueueCartMutation(win, async () => {
        const cart = await fetchCart(win);
        const inCart = cart.items.some((item) => String(item.variant_id) === String(variantId));
        const quantity = resolveShippingProtectionQuantity({ checked: desiredChecked, inCart });

        if (quantity !== null) {
          const isAdding = quantity === 1;
          const endpoint = isAdding
            ? `${win.routes?.cart_add_url || '/cart/add'}.js`
            : `${win.routes?.cart_change_url || '/cart/change'}.js`;
          const body = isAdding
            ? { items: [{ id: Number(variantId), quantity: 1 }] }
            : { id: String(variantId), quantity: 0 };
          const response = await win.fetch(endpoint, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          if (!response.ok) throw new Error(`Shipping protection update failed with ${response.status}`);
        }

        await refreshCartDrawer(doc, win);
      });
    } catch (error) {
      input.checked = !desiredChecked;
      setShippingError(doc, 'Shipping protection could not be updated. Try again.');
      console.error('[BRASA] Shipping protection update failed', error);
    } finally {
      if (input.isConnected) setShippingBusy(input, false);
    }
  }

  function mount(doc, win) {
    // Reconcile browser form-state restore (Firefox reload) with the server-preselected tier.
    const restoredTier = doc.querySelector('.quantity-break-input:checked');
    const preselectedTier = doc.querySelector('.quantity-break-option.selected .quantity-break-input');
    if (restoredTier && preselectedTier && restoredTier !== preselectedTier) {
      restoredTier.checked = false;
      preselectedTier.checked = true;
    }
    if (!doc || !win || doc.documentElement.dataset.brasaPurchaseFlowMounted === 'true') return;
    doc.documentElement.dataset.brasaPurchaseFlowMounted = 'true';
    markOneTimeOnlyContent(doc);

    const state = {
      initializedWidgets: new WeakSet(),
      lastOneTimeQuantity: Math.max(1, getSelectedQuantity(doc)),
      subscriptionActive: true,
      defaultSubscription: true,
      sellingPlanId: sellingPlanIdForQuantity(getSelectedQuantity(doc)),
      syncingTier: false,
      initializingWidget: false,
      purchaseUiSyncScheduled: false,
    };

    function selectTier(quantity) {
      const input = getTierInput(doc, quantity);
      if (!input) return;

      state.syncingTier = true;
      try {
        guardScroll(win, () => input.click());
        applyCartContract(doc, state.subscriptionActive ? 'subscription' : 'one_time', quantity);
      } finally {
        state.syncingTier = false;
      }
    }

    function mountSubscribeToggle() {
      const toggle = ensureSubscribeToggle(doc);
      if (!toggle) return;
      if (toggle.dataset.brasaWired !== 'true') {
        toggle.dataset.brasaWired = 'true';
        state.subscribeToggle = toggle;
        state.subscribeCheckbox = toggle.querySelector('.brasa-subscribe-checkbox');
        state.subscribeCheckbox?.addEventListener('change', () => {
          const wantsSubscription = state.subscribeCheckbox.checked;
          state.defaultSubscription = false;
          // Quantity/subscribe are decoupled: the selected tier is never
          // reset here. The plan id is derived from whatever quantity is
          // currently selected, so 1/2/3/4 tins each subscribe correctly.
          const currentQty = getSelectedQuantity(doc);
          setPurchaseMode(
            wantsSubscription ? 'subscription' : 'one_time',
            wantsSubscription ? sellingPlanIdForQuantity(currentQty) : '',
            { restorePrevious: false },
          );
          // Deliberately NOT driving Seal's hidden widget here: Seal only
          // knows the live product's single 30-day plan, and clicking its
          // radio would fire sealsubs:selling_plan_changed with that WRONG
          // plan id and clobber the state we just set above. The 4 new
          // tiered plans live entirely outside Seal, on the isolated staging
          // product/variant (see applyCartContract).
        });
      }
      syncSubscribeToggle();
    }

    function syncStickyCtaBound() { try { syncStickyCta(doc); } catch (e) {} }

    function syncSubscribeToggle() {
      syncStickyCtaBound();
      const checkbox = state.subscribeCheckbox;
      if (!checkbox) return;

      // Every tier (1-4 tins) is subscribable on the same Seal 30-day cadence,
      // so the checkbox stays checkable/checked regardless of the selected
      // quantity and never forces a tier change.
      const selectedQty = getSelectedQuantity(doc);

      if (checkbox.checked !== state.subscriptionActive) checkbox.checked = state.subscriptionActive;
      state.subscribeToggle?.classList.toggle('is-active', state.subscriptionActive);

      const amount = state.subscribeToggle?.querySelector('.brasa-toggle-amount');
      if (amount) {
        const sub = subscriptionTotal(doc);
        // Idempotent: writing textContent replaces the text node and would
        // register as a childList mutation even when the value is unchanged.
        const next = sub != null ? formatMoney(sub) : '';
        if (amount.textContent !== next) amount.textContent = next;
      }

      const per = state.subscribeToggle?.querySelector('.brasa-toggle-per');
      const days = cadenceDaysForQuantity(selectedQty);
      if (per) {
        const nextPer = `/${days}d`;
        if (per.textContent !== nextPer) per.textContent = nextPer;
      }

      const subCopy = state.subscribeToggle?.querySelector('.brasa-toggle-sub');
      if (subCopy) {
        const refillTotal = subscriptionTotal(doc);
        const nextSub = state.subscriptionActive && refillTotal != null
          ? `${formatMoney(refillTotal)} every ${days} days · Skip or cancel anytime`
          : '';
        if (subCopy.textContent !== nextSub) subCopy.textContent = nextSub;
      }
    }

    function setPurchaseMode(mode, sellingPlanId, { restorePrevious = false } = {}) {
      const selection = resolvePurchaseSelection({
        requestedMode: mode,
        requestedQuantity: getSelectedQuantity(doc),
        previousOneTimeQuantity: state.lastOneTimeQuantity,
        restorePrevious,
        sellingPlanId,
      });
      const widget = getSealWidget(doc);
      state.subscriptionActive = selection.mode === 'subscription';
      state.sellingPlanId = selection.sellingPlanId;

      if (selection.mode === 'subscription') {
        // Keep the selected tier; subscription just discounts it (every card).
        doc.documentElement.classList.add('brasa-subscription-active');
        setLadderPricesForMode(doc, true);
        updateTopPriceForSubscription(doc);
      } else {
        if (selection.quantity !== getSelectedQuantity(doc)) selectTier(selection.quantity);
        doc.documentElement.classList.remove('brasa-subscription-active');
        setLadderPricesForMode(doc, false);
        updateTopPriceFromTier(doc);
      }

      setCta(doc, selection.mode);
      applyCartContract(doc, selection.mode, selection.quantity);
      syncSubscribeToggle();
      schedulePurchaseUiSync();
    }

    function syncPurchaseUi() {
      const widget = getSealWidget(doc);
      const selectedQuantity = getSelectedQuantity(doc);

      if (state.subscriptionActive) {
        // The plan tracks whichever tier is currently selected, not the tier
        // that was active when the toggle was first checked.
        state.sellingPlanId = sellingPlanIdForQuantity(selectedQuantity);
        doc.documentElement.classList.add('brasa-subscription-active');
        applyCartContract(doc, 'subscription', selectedQuantity);
        setLadderPricesForMode(doc, true);
        updateTopPriceForSubscription(doc);
        setCta(doc, 'subscription');
        syncSubscribeToggle();
        return;
      }

      state.lastOneTimeQuantity = selectedQuantity;
      doc.documentElement.classList.remove('brasa-subscription-active');
      applyCartContract(doc, 'one_time', selectedQuantity);
      setLadderPricesForMode(doc, false);
      updateTopPriceFromTier(doc);
      setCta(doc, 'one_time');
      syncSubscribeToggle();
    }

    function schedulePurchaseUiSync() {
      if (state.purchaseUiSyncScheduled) return;
      state.purchaseUiSyncScheduled = true;

      const run = () => {
        state.purchaseUiSyncScheduled = false;
        syncPurchaseUi();
      };

      win.setTimeout(run, 0);
    }

    function initializeWidget(widget) {
      if (!isPrimarySealWidget(doc, widget) || state.initializedWidgets.has(widget)) return;
      state.initializedWidgets.add(widget);
      normalizeSealCopy(widget);
      state.initializingWidget = true;
      const planId = state.subscriptionActive ? (state.sellingPlanId || sellingPlanIdForQuantity(getSelectedQuantity(doc))) : '';
      dispatchSellingPlan(widget, planId);
      win.setTimeout(() => {
        state.initializingWidget = false;
        setPurchaseMode(planId ? 'subscription' : 'one_time', planId, { restorePrevious: false });
      }, 0);
    }

    doc.addEventListener('sealsubs:subscription_widget_created', (event) => {
      const eventWidget = event.detail?.element || getSealWidget(doc);
      if (!isPrimarySealWidget(doc, eventWidget)) return;
      initializeWidget(eventWidget);
    });

    doc.addEventListener('sealsubs:selling_plan_changed', (event) => {
      const eventWidget = event.detail?.element || getSealWidget(doc);
      if (!isPrimarySealWidget(doc, eventWidget) || state.initializingWidget) return;
      const sellingPlanId = String(event.detail?.selling_plan_id || '');
      if (!sellingPlanId && state.defaultSubscription) return;
      normalizeSealCopy(eventWidget);

      if (sellingPlanId) {
        setPurchaseMode('subscription', sellingPlanId);
      } else {
        setPurchaseMode('one_time', '', {
          restorePrevious: state.subscriptionActive && !state.initializingWidget,
        });
      }
    });

    doc.addEventListener('quantity_break:changed', () => {
      if (state.syncingTier) return;
      schedulePurchaseUiSync();
    });

    doc.addEventListener('change', (event) => {
      const tierInput = event.target.closest?.('.quantity-break-input');
      if (tierInput && !state.syncingTier) {
        const quantity = Math.max(1, Number.parseInt(tierInput.value, 10) || 1);
        if (state.subscriptionActive) {
          // Keep the subscription; the Seal plan covers every tier, so only
          // the quantity changes and the cart form stays pointed at the
          // subscribable variant.
          state.sellingPlanId = sellingPlanIdForQuantity(quantity);
          applyCartContract(doc, 'subscription', quantity);
          updateTopPriceForSubscription(doc);
          syncSubscribeToggle();
        } else {
          state.lastOneTimeQuantity = quantity;
          updateTopPriceFromTier(doc);
          applyCartContract(doc, 'one_time', quantity);
        }
      }

      const shippingInput = event.target.closest?.('.gb-shipping-protection-button input[type="checkbox"]');
      if (shippingInput) updateShippingProtection(shippingInput, doc, win);
    });

    doc.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form.matches?.('.shop-add-to-cart-wrapper form[action*="/cart/add"]')) return;

      const quantity = getSelectedQuantity(doc);

      // Final safety net: whatever other scripts did to the form after the
      // last sync, the submitted payload is exactly the resolved contract.
      if (state.subscriptionActive) {
        state.sellingPlanId = sellingPlanIdForQuantity(quantity);
        applyCartContract(doc, 'subscription', quantity);
      } else {
        applyCartContract(doc, 'one_time', quantity);
      }
    }, true);

    // The theme's sticky bar adds #sticky-variant-id with quantity 1 and no
    // selling plan through cart-drawer.addItemToCart, which would bypass the
    // contract for subscriptions. Route it through the PDP form instead.
    win.addToCartSticky = function addToCartStickyViaContract() {
      const form = doc.querySelector('.shop-add-to-cart-wrapper form[action*="/cart/add"]');
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.querySelector('[type="submit"]')?.click();
    };

    // This callback mutates the DOM (mounts the subscribe toggle, rewrites the
    // price text), and it watches doc.body { childList, subtree } — so its own
    // writes would re-trigger it forever, starving the event loop and leaving
    // the page perpetually "loading". Disconnect around our own writes, run the
    // sync inline (not deferred, so the mutations land inside the disconnected
    // window), then drop the records we generated. We stay reactive to genuinely
    // new mutations, e.g. Seal re-injecting its widget.
    let handlingSealMutations = false;
    const observer = new win.MutationObserver(() => {
      if (handlingSealMutations) return;
      handlingSealMutations = true;
      observer.disconnect();
      try {
        const widget = getSealWidget(doc);
        if (widget) initializeWidget(widget);
        mountSubscribeToggle();
        syncPurchaseUi();
      } finally {
        observer.takeRecords();
        observer.observe(doc.body, { childList: true, subtree: true });
        handlingSealMutations = false;
      }
    });
    observer.observe(doc.body, { childList: true, subtree: true });

    const existingWidget = getSealWidget(doc);
    if (existingWidget) initializeWidget(existingWidget);
    mountSubscribeToggle();
    syncPurchaseUi();
    doc.documentElement.classList.add('brasa-purchase-ready');
  }

  return {
    COPY,
    mount,
    resolvePurchaseSelection,
    resolveShippingProtectionQuantity,
    setGiftBarsForMode,
    subscriptionUnitPrice,
    sellingPlanIdForQuantity,
    cadenceDaysForQuantity,
    SUBSCRIPTION_STAGING_VARIANT_ID,
    SUBSCRIPTION_PLAN_IDS_BY_QTY,
    ONE_TIME_VARIANT_IDS_BY_QTY,
    resolveCartContract,
    applyCartContract,
  };
});
